/**
 * Timesheet — spec §5-F9, §8.1 `/timesheet`.
 *
 * Week grid with per-day and per-project totals, plus submit-for-approval.
 * Estimate-vs-actual lives on the task; this screen answers "where did my week
 * go", which is a different question.
 */

import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Send, Square, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { useCan } from '@/features/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  useDeleteTimeEntry,
  useRunningTimer,
  useStopTimer,
  useSubmitTimesheet,
  useTimeEntries,
  useTimesheet,
} from './api';

/**
 * Monday of the week containing `date`, as a UTC-midnight Date.
 *
 * UTC throughout: the server bins timesheet days in UTC, and mixing a local
 * week boundary with UTC binning produced an eight-column week east of
 * Greenwich.
 */
function weekStart(date: Date): Date {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = (d.getUTCDay() + 6) % 7; // 0 = Monday
  d.setUTCDate(d.getUTCDate() - day);
  return d;
}

const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

export function formatMinutes(minutes: number): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function TimesheetPage() {
  const can = useCan();
  const [anchor, setAnchor] = useState(() => weekStart(new Date()));
  const from = anchor;
  const to = useMemo(() => {
    const end = new Date(anchor);
    end.setUTCDate(end.getUTCDate() + 6);
    return end;
  }, [anchor]);

  const { data: sheet, isLoading } = useTimesheet(isoDate(from), isoDate(to));
  const { data: entries = [] } = useTimeEntries(isoDate(from), isoDate(to));
  const { data: running } = useRunningTimer();
  const stop = useStopTimer();
  const remove = useDeleteTimeEntry();
  const submit = useSubmitTimesheet();

  const days = sheet?.days ?? [];
  const maxMinutes = Math.max(60, ...days.map((d) => d.minutes));

  return (
    <>
      <PageHeader
        title="Timesheet"
        subtitle={`${isoDate(from)} – ${isoDate(to)}`}
        actions={
          <>
            {running && (
              <Button
                variant="subtle"
                size="sm"
                onClick={async () => {
                  await stop.mutateAsync();
                  toast.success('Timer stopped');
                }}
              >
                <Square />
                Stop timer
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Previous week"
              onClick={() => {
                const prev = new Date(anchor);
                prev.setUTCDate(prev.getUTCDate() - 7);
                setAnchor(prev);
              }}
            >
              <ChevronLeft />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAnchor(weekStart(new Date()))}>
              This week
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Next week"
              onClick={() => {
                const next = new Date(anchor);
                next.setUTCDate(next.getUTCDate() + 7);
                setAnchor(next);
              }}
            >
              <ChevronRight />
            </Button>
            {/* Needs `time.log`; a guest holds none (§3.1). */}
            {can(PERMISSIONS.TIME_LOG) && (
              <Button
                variant="primary"
                size="sm"
                disabled={!sheet?.totalMinutes || submit.isPending}
                onClick={async () => {
                  const result = await submit.mutateAsync({ from: isoDate(from), to: isoDate(to) });
                  toast.success(`Submitted ${result.submitted} entries for approval`);
                }}
              >
                <Send />
                Submit week
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-4 p-4">
        {isLoading ? (
          <Skeleton className="h-40" />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="vt-surface p-3">
                <div className="text-[11px] uppercase tracking-wide vt-muted">Logged this week</div>
                <div className="vt-mono text-[28px] font-semibold" data-testid="week-total">
                  {formatMinutes(sheet?.totalMinutes ?? 0)}
                </div>
              </div>
              <div className="vt-surface p-3">
                <div className="text-[11px] uppercase tracking-wide vt-muted">Submitted</div>
                <div className="vt-mono text-[28px] font-semibold">
                  {formatMinutes(sheet?.submittedMinutes ?? 0)}
                </div>
              </div>
              <div className="vt-surface p-3">
                <div className="text-[11px] uppercase tracking-wide vt-muted">Approved</div>
                <div className="vt-mono text-[28px] font-semibold">
                  {formatMinutes(sheet?.approvedMinutes ?? 0)}
                </div>
              </div>
            </div>

            {/* Week grid — a bar per day, working days highlighted. */}
            <div className="vt-surface p-3" data-testid="week-grid">
              <div className="grid grid-cols-7 gap-2">
                {days.map((day) => {
                  const date = new Date(`${day.date}T00:00:00`);
                  return (
                    <div key={day.date} className="flex flex-col items-center gap-1">
                      <div className="text-[11px] vt-muted">
                        {date.toLocaleDateString(undefined, { weekday: 'short' })}
                      </div>
                      <div
                        className="flex h-24 w-full items-end rounded-[6px]"
                        style={{ background: day.isWorkingDay ? 'var(--surface-2)' : 'transparent' }}
                        title={`${day.date}: ${formatMinutes(day.minutes)}`}
                      >
                        <div
                          className="w-full rounded-[6px] transition-[height] duration-[180ms]"
                          style={{
                            height: `${Math.round((day.minutes / maxMinutes) * 100)}%`,
                            background: day.minutes ? 'var(--accent)' : 'transparent',
                          }}
                        />
                      </div>
                      <div className="vt-mono text-[11px]">{formatMinutes(day.minutes)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {(sheet?.byProject.length ?? 0) > 0 && (
              <div className="vt-surface overflow-hidden">
                <div className="border-b border-[var(--border)] px-3 py-2 text-[11px] font-medium uppercase tracking-wide vt-muted">
                  By project
                </div>
                {sheet!.byProject.map((p) => (
                  <div
                    key={p.projectId}
                    className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 last:border-0"
                  >
                    <span className="vt-mono text-[11px] vt-muted">{p.projectKey}</span>
                    <span className="flex-1 truncate text-[13px]">{p.projectName}</span>
                    <span className="vt-mono text-[13px] font-medium">{formatMinutes(p.minutes)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="vt-surface overflow-hidden">
              <div className="border-b border-[var(--border)] px-3 py-2 text-[11px] font-medium uppercase tracking-wide vt-muted">
                Entries
              </div>
              {entries.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon={<Timer className="size-6" />}
                    title="Nothing logged this week"
                    description="Press T on any task to start a timer, or log time from the task detail page."
                  />
                </div>
              ) : (
                entries.map((entry) => (
                  <div
                    key={entry._id}
                    className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 last:border-0"
                  >
                    <Clock className="size-3.5 shrink-0 text-[var(--text-muted)]" />
                    <span className="vt-mono text-[11px] vt-muted">
                      {new Date(entry.startedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      {entry.taskKey ? (
                        <span className="vt-mono mr-1 vt-muted">{entry.taskKey}</span>
                      ) : null}
                      {entry.taskTitle ?? entry.description ?? 'Time entry'}
                    </span>
                    {entry.approvalStatus !== 'pending' && (
                      <Badge tone={entry.approvalStatus === 'approved' ? 'accent' : 'neutral'}>
                        {entry.approvalStatus}
                      </Badge>
                    )}
                    <span className="vt-mono shrink-0 text-[13px] font-medium">
                      {formatMinutes(entry.durationMinutes)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Delete entry"
                      onClick={() => remove.mutate(entry._id)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
